import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import {
  DEFAULT_SOUND_PREFERENCES,
  SOUND_EFFECTS,
  mrmSoundEngine,
  normalizeSoundPreferences,
  type SoundEffect,
  type SoundPreferences,
} from './soundEngine';

const STORAGE_KEY = 'mrm.sound.release1.98.preferences';

interface SoundContextValue {
  preferences: SoundPreferences;
  play: (effect: SoundEffect) => Promise<boolean>;
  setEnabled: (enabled: boolean) => void;
  setVolume: (volume: number) => void;
  setQuietMode: (quietMode: boolean) => void;
  setHapticsEnabled: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;
}

const SoundContext = createContext<SoundContextValue | null>(null);

function loadPreferences(): SoundPreferences {
  if (typeof window === 'undefined') return DEFAULT_SOUND_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return normalizeSoundPreferences(raw ? JSON.parse(raw) as Partial<SoundPreferences> : null);
  } catch {
    return DEFAULT_SOUND_PREFERENCES;
  }
}

function soundFromTarget(target: EventTarget | null): SoundEffect | 'none' | undefined {
  if (!(target instanceof Element)) return undefined;
  const interactive = target.closest<HTMLElement>('button, a, summary, [role="button"]');
  if (!interactive || interactive.matches('[disabled], [aria-disabled="true"]')) return undefined;
  if (interactive.dataset.reaction) return 'none';
  const explicit = interactive.dataset.sound;
  if (explicit === 'none') return 'none';
  if (explicit && SOUND_EFFECTS.includes(explicit as SoundEffect)) return explicit as SoundEffect;
  return 'tap';
}

export function SoundProvider({ children }: PropsWithChildren) {
  const [preferences, setPreferences] = useState<SoundPreferences>(loadPreferences);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences)); } catch { /* storage may be unavailable */ }
  }, [preferences]);

  const play = useCallback((effect: SoundEffect) => mrmSoundEngine.play(effect, preferences), [preferences]);

  useEffect(() => {
    const handlePointer = (event: PointerEvent) => {
      const effect = soundFromTarget(event.target);
      if (!effect || effect === 'none') return;
      void play(effect);
    };
    const handleKeyboard = (event: KeyboardEvent) => {
      if (event.repeat || !['Enter', ' '].includes(event.key)) return;
      const effect = soundFromTarget(event.target);
      if (!effect || effect === 'none') return;
      void play(effect);
    };
    document.addEventListener('pointerup', handlePointer, true);
    document.addEventListener('keydown', handleKeyboard, true);
    return () => {
      document.removeEventListener('pointerup', handlePointer, true);
      document.removeEventListener('keydown', handleKeyboard, true);
    };
  }, [play]);

  const setEnabled = useCallback((enabled: boolean) => {
    setPreferences((current) => current.enabled === enabled ? current : { ...current, enabled });
  }, []);

  const setVolume = useCallback((volume: number) => {
    setPreferences((current) => {
      const next = normalizeSoundPreferences({ ...current, volume });
      return next.volume === current.volume ? current : next;
    });
  }, []);

  const setQuietMode = useCallback((quietMode: boolean) => {
    setPreferences((current) => current.quietMode === quietMode ? current : { ...current, quietMode });
  }, []);

  const setHapticsEnabled = useCallback((hapticsEnabled: boolean) => {
    setPreferences((current) => current.hapticsEnabled === hapticsEnabled ? current : { ...current, hapticsEnabled });
  }, []);

  const setReducedMotion = useCallback((reducedMotion: boolean) => {
    setPreferences((current) => current.reducedMotion === reducedMotion ? current : { ...current, reducedMotion });
    if (typeof document !== 'undefined') document.documentElement.dataset.mrmReducedMotion = reducedMotion ? 'true' : 'false';
  }, []);

  useEffect(() => {
    document.documentElement.dataset.mrmReducedMotion = preferences.reducedMotion ? 'true' : 'false';
  }, [preferences.reducedMotion]);

  const value = useMemo<SoundContextValue>(() => ({
    preferences,
    play,
    setEnabled,
    setVolume,
    setQuietMode,
    setHapticsEnabled,
    setReducedMotion,
  }), [play, preferences, setEnabled, setHapticsEnabled, setQuietMode, setReducedMotion, setVolume]);

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
}

export function useSound(): SoundContextValue {
  const context = useContext(SoundContext);
  if (!context) throw new Error('useSound must be used inside SoundProvider');
  return context;
}
